import React, { useState, useEffect } from 'react';
import loadScript from 'load-script2';
import { loadCSS } from 'fg-loadcss';

let calendlyLoadState = 'unloaded';

declare global {
  interface Window {
    Calendly: { showPopupWidget: (url: string) => void };
  }
}

function useCalendly(newBookingUrl?: string) {
  const [myState, setMyState] = useState({ loadCalendlyAttempts: 0 });
  const [bookingUrl, setBookingUrl] = useState<string>(newBookingUrl || '');

  useEffect(() => {
    if (calendlyLoadState === 'unloaded') {
      calendlyLoadState = 'loading';
      loadScript('https://calendly.com/assets/external/widget.js')
        .then(() => loadCSS('https://calendly.com/assets/external/widget.css'))
        .then(() => {
          calendlyLoadState = 'loaded';
        })
        .catch((error: unknown) => {
          // Calendly is third-party and blockable - by an ad blocker, a
          // school network, or simply being offline. Without this the
          // rejection is unhandled.
          //
          // Resetting to 'unloaded' does NOT make the retry below re-attempt
          // the load: this effect's deps are [], so it never runs again. The
          // retries only re-check the state and then surface the error.
          // Recovering properly needs the load sequence pulled out into a
          // callable loader - unlike openCalendly.tsx, whose effect depends on
          // myState and so does re-fire on each retry.
          console.warn('Could not load the Calendly widget:', error);
          calendlyLoadState = 'unloaded';
        });
    }
  }, []);

  useEffect(() => {
    if (newBookingUrl) {
      setBookingUrl(newBookingUrl);
    }
  }, [newBookingUrl]);

  function openCalendly(onShowError?: (msg: string) => void) {
    if (calendlyLoadState === 'loaded') {
      window.Calendly.showPopupWidget(bookingUrl);
    } else if (myState.loadCalendlyAttempts > 2) {
      setMyState({ loadCalendlyAttempts: 0 });
      calendlyLoadState = 'unloaded';
      if (onShowError) {
        onShowError('Sorry, the booking calendar isn’t available at the moment. Please try again later.');
      }
    } else {
      setTimeout(() => {
        setMyState({ loadCalendlyAttempts: myState.loadCalendlyAttempts + 1 });
        openCalendly(onShowError);
      }, 1000);
    }
  }

  return openCalendly;
}

type ResponsiveValue =
  | string
  | number
  | { desktop?: string | number; tablet?: string | number; mobile?: string | number };

function getResponsiveValue(value: ResponsiveValue | undefined, device: 'desktop' | 'tablet' | 'mobile') {
  if (value == null) return undefined;
  if (typeof value === 'string' || typeof value === 'number') return value;
  return value[device] ?? value.desktop ?? value.tablet ?? value.mobile;
}

function useDeviceType() {
  const [device, setDevice] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');

  useEffect(() => {
    function updateDevice() {
      if (window.innerWidth <= 768) {
        setDevice('mobile');
      } else if (window.innerWidth <= 1024) {
        setDevice('tablet');
      } else {
        setDevice('desktop');
      }
    }
    updateDevice();
    window.addEventListener('resize', updateDevice);
    return () => window.removeEventListener('resize', updateDevice);
  }, []);

  return device;
}

interface CalendlyComponentProps {
  bookingUrl: string;

  buttonText?: ResponsiveValue;
  buttonColor?: ResponsiveValue;
  buttonTextColor?: ResponsiveValue;
  buttonWidth?: ResponsiveValue;
  buttonHeight?: ResponsiveValue;
  buttonFontFamily?: ResponsiveValue;
  buttonFontSize?: ResponsiveValue;
}

export function CalendlyComponent({
  bookingUrl,

  buttonText = 'Book a Demo',
  buttonColor = '#00aaff',
  buttonTextColor = '#fff',
  buttonWidth = { desktop: '200px', tablet: '160px', mobile: '140px' },
  buttonHeight = { desktop: '48px', tablet: '40px', mobile: '36px' },
  buttonFontFamily = 'Arial, sans-serif',
  buttonFontSize = { desktop: 16, tablet: 14, mobile: 14 },
}: CalendlyComponentProps) {
  const openCalendly = useCalendly(bookingUrl);
  const [error, setError] = useState<string | null>(null);
  const device = useDeviceType();

  const width = getResponsiveValue(buttonWidth, device);
  const height = getResponsiveValue(buttonHeight, device);
  const fontFamily = getResponsiveValue(buttonFontFamily, device);
  const fontSize = getResponsiveValue(buttonFontSize, device);
  const bgColor = getResponsiveValue(buttonColor, device);
  const txtColor = getResponsiveValue(buttonTextColor, device);
  const text = getResponsiveValue(buttonText, device);

  return (
    <>
      <button
        onClick={() => openCalendly(setError)}
        style={{
          width,
          height,
          backgroundColor: bgColor,
          color: txtColor,
          border: 'none',
          borderRadius: 4,
          cursor: 'pointer',
          fontFamily,
          fontSize,
        }}
      >
        {text}
      </button>
      {error && <div style={{ color: 'red', marginTop: 8 }}>{error}</div>}
    </>
  );
}
